import { createSimpleStore } from './factories/simpleStoreFactory';

const STORAGE_KEY = 'jobSelectedDate';

/**
 * Loads the persisted date from localStorage
 * Returns null if no date is stored or if parsing fails
 */
function loadPersistedDate(): Date | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed = JSON.parse(stored);
    if (parsed && typeof parsed === 'string') {
      const date = new Date(parsed);
      // Validate the date is not invalid
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
  } catch (error) {
    console.warn('Failed to load persisted job date from localStorage:', error);
  }

  return null;
}

/**
 * Persists the date to localStorage as ISO string
 */
function persistDate(date: Date | null): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (date) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(date.toISOString()));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.warn('Failed to persist job date to localStorage:', error);
  }
}

type JobDateState = {
  selectedDate: Date | null;
};

type JobDateActions = {
  setSelectedDate: (date: Date | null) => void;
  resetSelectedDate: () => void;
};

export type JobDateStore = JobDateState & JobDateActions;

export const useJobDateStore = createSimpleStore<JobDateState, JobDateActions>({
  name: 'JobDateStore',
  initialState: {
    // Initialize from localStorage if available
    selectedDate: loadPersistedDate()
  },
  actions: (set) => ({
    setSelectedDate: (date: Date | null) => {
      set({ selectedDate: date });
      persistDate(date);
    },
    resetSelectedDate: () => {
      set({ selectedDate: null });
      persistDate(null);
    }
  })
});
